import type { AnyFieldApi } from '@tanstack/react-form'
import type { ApiErrorBody } from '@/api/client'
import type { Machine } from '@/api/resources/machines'
import type { Organisation } from '@/api/resources/organisations'
import { formatFieldErrors } from '@/components/forms/field-errors'
import { FormInput } from '@/components/forms/form-input'
import { FormSwitch } from '@/components/forms/form-switch'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'

interface AddOperatorFormProps {
  // eslint-disable-next-line ts/no-explicit-any
  form: any
  machines: Machine[] | undefined
  serverErrors?: ApiErrorBody['errors']
  organisations?: Organisation[]
  selectedOrganisationId?: string
  onOrganisationChange?: (value: string) => void
}

export function AddOperatorForm({
  form,
  machines,
  serverErrors,
  organisations,
  selectedOrganisationId,
  onOrganisationChange,
}: AddOperatorFormProps): React.ReactElement {
  const showOrganisationSelect = organisations !== undefined && organisations.length > 1

  return (
    <div className="space-y-4">
      {showOrganisationSelect && (
        <div className="space-y-2">
          <Label htmlFor="organisation-select">Organisation</Label>
          <Select value={selectedOrganisationId} onValueChange={onOrganisationChange}>
            <SelectTrigger id="organisation-select" className="w-full">
              <SelectValue placeholder="Select an organisation" />
            </SelectTrigger>
            <SelectContent>
              {organisations.map((organisation) => (
                <SelectItem key={organisation.id} value={String(organisation.id)}>
                  {organisation.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <form.Field name="first_name">
        {(field: AnyFieldApi) => (
          <FormInput
            field={field}
            label="First name"
            placeholder="Laura"
            serverErrors={serverErrors}
          />
        )}
      </form.Field>

      <form.Field name="last_name">
        {(field: AnyFieldApi) => (
          <FormInput
            field={field}
            label="Last name"
            placeholder="van den Berg"
            serverErrors={serverErrors}
          />
        )}
      </form.Field>

      <form.Field name="email">
        {(field: AnyFieldApi) => (
          <FormInput
            field={field}
            label="Email"
            type="email"
            placeholder="laura@example.com"
            serverErrors={serverErrors}
          />
        )}
      </form.Field>

      <form.Field name="email_optin">
        {(field: AnyFieldApi) => (
          <FormSwitch
            field={field}
            label="Email notifications"
            description="Notify operator when the machine can be refilled."
            serverErrors={serverErrors}
          />
        )}
      </form.Field>

      {machines !== undefined && machines.length > 0 && (
        <form.Field name="machine_ids">
          {(field: AnyFieldApi) => {
            const selectedIds: number[] = Array.isArray(field.state.value)
              ? (field.state.value as number[])
              : []

            function handleMachineToggle(machineId: number, checked: boolean): void {
              const next = checked
                ? [...selectedIds, machineId]
                : selectedIds.filter((id) => id !== machineId)
              field.handleChange(next)
            }

            const allSelected = machines.length > 0 && selectedIds.length === machines.length

            function handleSelectAll(): void {
              field.handleChange(allSelected ? [] : machines!.map((m) => m.id))
            }

            const clientErrors = formatFieldErrors(field.state.meta.errors)
            const serverMessages = serverErrors?.machine_ids ?? []
            const allErrors = [...clientErrors, ...serverMessages]

            return (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Machines</Label>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={handleSelectAll}
                  >
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </Button>
                </div>
                <div className="max-h-48 divide-y overflow-y-auto rounded-md border px-3">
                  {machines.map((machine) => (
                    <div key={machine.id} className="flex items-center gap-3 py-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{machine.name}</p>
                        <p className="text-muted-foreground text-xs">
                          {machine.code}
                          {machine.location && <span> · {machine.location.name}</span>}
                        </p>
                      </div>
                      <Switch
                        checked={selectedIds.includes(machine.id)}
                        onCheckedChange={(checked) => handleMachineToggle(machine.id, checked)}
                      />
                    </div>
                  ))}
                </div>
                {allErrors.length > 0 && (
                  <p id="machine_ids-error" className="text-destructive text-sm">
                    {allErrors.join(', ')}
                  </p>
                )}
              </div>
            )
          }}
        </form.Field>
      )}
    </div>
  )
}
